package br.gov.go.pge.permanenciaweb.site.components

import br.gov.go.pge.permanenciaweb.shared.CountsDto
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import com.varabyte.kobweb.silk.style.CssStyleVariant

/*
 * Os cards do topo também são filtros (e novos cards entram aqui).
 *
 * Um card com [KpiCard.quickFilterId] liga/desliga um filtro do painel e mostra a contagem das
 * linhas que sobreviveram a todos os filtros ativos (semântica de "filtro de visual" do Power BI).
 * Sem `quickFilterId` o card é global (equivalente a `ALL()`): mostra sempre o número do snapshot da
 * construção e não filtra nada.
 *
 * Adicionar um card = uma entrada em [KPI_CARDS]; se ele for filtrável, também um valor em
 * [QuickFilterId] e um predicado em [KpiCard.matches].
 */

internal enum class QuickFilterId { SEM_LOCALIZACAO, INCONSISTENTE, SEM_SAIDA, LIBERADO, SEM_LIBERACAO }

internal data class KpiCard(
    /** Slug estável para `data-kpi-card`/`data-quick-filter` (sem acento, kebab-case). */
    val id: String,
    val label: String,
    val valueVariant: CssStyleVariant<KpiValueKind>,
    /**
     * Número global do snapshot, usado pelos cards que não filtram. Cards puramente filtráveis
     * podem omiti-lo: a contagem exibida vem de [matches] sobre as linhas atuais.
     */
    val globalCount: ((CountsDto) -> Int)? = null,
    val quickFilterId: QuickFilterId? = null,
    /** Predicado das linhas; quando há [globalCount], precisa reproduzir a mesma condição. */
    val matches: (OccupantDto) -> Boolean = { true },
)

/** Igual a `Counts.semAndar` (`RosterPolicy.kt:118`): andar nulo/em branco; `sala` não entra. */
internal fun OccupantDto.semLocalizacao(): Boolean = andar.isNullOrBlank()


internal val KPI_CARDS: List<KpiCard> = listOf(
    KpiCard(
        id = "possiveis-no-predio",
        label = "Possíveis no prédio",
        valueVariant = KpiValueSteelVariant,
        globalCount = { it.occupants },
    ),
    KpiCard(
        id = "sem-localizacao",
        label = "Sem localização",
        valueVariant = KpiValueOrangeVariant,
        globalCount = { it.semAndar },
        quickFilterId = QuickFilterId.SEM_LOCALIZACAO,
        matches = OccupantDto::semLocalizacao,
    ),
    KpiCard(
        id = "com-inconsistencia",
        label = "Com inconsistência",
        valueVariant = KpiValueRedVariant,
        globalCount = { it.inconsistentes },
        quickFilterId = QuickFilterId.INCONSISTENTE,
        matches = OccupantDto::inconsistente,
    ),
    KpiCard(
        id = "sem-saida",
        label = "Sem saída",
        valueVariant = KpiValueOrangeVariant,
        quickFilterId = QuickFilterId.SEM_SAIDA,
        matches = OccupantDto::semSaida,
    ),
    KpiCard(
        id = "liberados",
        label = "Liberados",
        valueVariant = KpiValueSteelVariant,
        globalCount = { it.liberados },
        quickFilterId = QuickFilterId.LIBERADO,
        matches = OccupantDto::liberado,
    ),
    KpiCard(
        // Inverso do alerta "Liberado": é quem ainda precisa ser procurado na ronda.
        id = "sem-liberacao",
        label = "Sem liberação",
        valueVariant = KpiValueSteelVariant,
        globalCount = { it.occupants - it.liberados },
        quickFilterId = QuickFilterId.SEM_LIBERACAO,
        matches = { !it.liberado() },
    ),
    KpiCard(
        id = "removidos-hoje",
        label = "Removidos hoje",
        valueVariant = KpiValueRedVariant,
        globalCount = { it.removed },
    ),
)

internal fun kpiCard(id: QuickFilterId): KpiCard = KPI_CARDS.first { it.quickFilterId == id }

/** AND entre os cards ativos; conjunto vazio é a identidade. */
internal fun List<OccupantDto>.applyQuickFilters(active: Set<QuickFilterId>): List<OccupantDto> {
    if (active.isEmpty()) return this
    val predicates = active.map { kpiCard(it).matches }
    return filter { o -> predicates.all { it(o) } }
}

/** Quantas linhas [this] tem por card filtrável — a contagem exibida com os filtros ativos. */
internal fun List<OccupantDto>.quickFilterCounts(): Map<QuickFilterId, Int> =
    QuickFilterId.entries.associateWith { id -> count(kpiCard(id).matches) }
